// assets/section-utils.js

export class SectionId {
  static #separator = '__';

  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[0];
  }

  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split(SectionId.#separator)[1];
  }

  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}${SectionId.#separator}${sectionName}`;
  }
}

export class HTMLUpdateUtility {
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.style.display = 'none';

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

export function getSectionsToRender() {
  return [
    { id: 'cart-drawer', selector: '#CartDrawer' },
    { id: 'cart-icon-bubble' },
  ];
}


export function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}


export const requestIdleCallback =
  typeof window.requestIdleCallback == 'function' ? window.requestIdleCallback : setTimeout;


export function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

export function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

export function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

export function applySmartGridWidths(container = document, totalColumns = 16) {

  const screenWidth = window.innerWidth;

  if (screenWidth <= 600) {
    totalColumns = 4;
  } else if (screenWidth <= 768) {
    totalColumns = 8;
  } else if (screenWidth <= 1400) {
    totalColumns = 12;
  }
  container.querySelectorAll('[data-smart-grid]').forEach((gridContainer) => {
    const gridWidth = gridContainer.clientWidth;

    // Optional: also expose the width as a CSS variable
    gridContainer.style.setProperty('--smart-grid-block-width', `${gridWidth}px`);
    gridContainer.style.setProperty('--smart-grid-column', `${totalColumns}`);

  });
}
export class Animations {

  static cardObserver = null;
  static textObserver = null;

  static cardAnimation(elements) {
    if (!elements || !elements.length) return;

    const isInViewport = el => {
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };

    // disconnect any existing observer to avoid duplicates/leaks
    if (Animations.cardObserver) {
      try { Animations.cardObserver.disconnect(); } catch (e) {}
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('card-animate');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    Animations.cardObserver = observer;

    elements.forEach(el => {
      el.classList.remove('card-animate', 'animation-ended');
      if (isInViewport(el)) {
        el.classList.add('card-animate');
      } else {
        observer.observe(el);
      }
    });
  }

  static textAnimation(targets) {
    if (!targets || !targets.length) return;

    const isInViewport = el => {
      const rect = el.getBoundingClientRect();
      return rect.top < window.innerHeight && rect.bottom > 0;
    };

    // disconnect any existing observer to avoid duplicates/leaks
    if (Animations.textObserver) {
      try { Animations.textObserver.disconnect(); } catch (e) {}
    }

    const observer = new IntersectionObserver((entries, obs) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('text-animate');
          obs.unobserve(entry.target);
        }
      });
    }, { threshold: 0.2 });

    Animations.textObserver = observer;

    targets.forEach(target => {
      target.classList.remove('text-animate', 'animation-ended');
      if (isInViewport(target)) {
        target.classList.add('text-animate');
      } else {
        observer.observe(target);
      }
    });
  }

  static #initialized = false;
  static initAll() {
  
    const cardElements = document.querySelectorAll("[data-js-animate]");
    Animations.cardAnimation(cardElements);

    const splitTargets = document.querySelectorAll("[data-split-inview]");
    Animations.textAnimation(splitTargets);
  }

  static destroy() {
    // Disconnect observers
    if (Animations.cardObserver) {
      try { Animations.cardObserver.disconnect(); } catch (e) {}
      Animations.cardObserver = null;
    }
    if (Animations.textObserver) {
      try { Animations.textObserver.disconnect(); } catch (e) {}
      Animations.textObserver = null;
    }

    // Reset classes
    document.querySelectorAll('[data-js-animate]').forEach((el) => {
      el.classList.remove('card-animate', 'animation-ended');
    });
    document.querySelectorAll('[data-split-inview]').forEach((el) => {
      el.classList.remove('text-animate', 'animation-ended');
    });
  }
}

class ScrollDirectionHandler {
  constructor() {
    this.lastScrollPosition = 0;
    this.scrollDirection = '';
    this.init();
    this.initSectionObserver();
  }

  init() {
    this.handleScroll = this.handleScroll.bind(this);
    window.addEventListener('scroll', this.handleScroll);
  }

  handleScroll() {
    const currentScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    if (currentScrollPosition > this.lastScrollPosition) {
      this.updateScrollDirection('down');
    } else if (currentScrollPosition < this.lastScrollPosition) {
      this.updateScrollDirection('up');
    }

    this.lastScrollPosition = Math.max(0, currentScrollPosition);
  }

  updateScrollDirection(direction) {
    if (this.scrollDirection !== direction) {
      this.scrollDirection = direction;
      document.body.setAttribute('data-scroll-direction', direction);
    }
  }

  initSectionObserver() {
    const sections = document.querySelectorAll('section');

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        const section = entry.target;
        const rect = entry.boundingClientRect;
        const viewportHeight = window.innerHeight || document.documentElement.clientHeight;

        if (this.scrollDirection === 'down') {
          if (rect.top >= 0 && rect.top <= viewportHeight) {
            section.classList.add('section-in-view');
          }
          if (rect.bottom < 0 || rect.top >= viewportHeight) {
            section.classList.remove('section-in-view');
          }
        }

        if (this.scrollDirection === 'up') {
          if (rect.bottom >= 0 && rect.bottom <= viewportHeight) {
            section.classList.add('section-in-view');
          }
          if (rect.top > viewportHeight || rect.bottom <= 0) {
            section.classList.remove('section-in-view');
          }
        }

        if (
          rect.top < viewportHeight &&
          rect.bottom > 0 &&
          !section.classList.contains('section-in-view')
        ) {
          section.classList.add('section-in-view');
        }
      });
    }, {
      threshold: [0],
    });

    sections.forEach(section => observer.observe(section));
  }

  destroy() {
    window.removeEventListener('scroll', this.handleScroll);
  }
}
// Initialize the class
const scrollHandler = new ScrollDirectionHandler();

const resizeCallbacks = new Set();
let resizeTimeout;

window.addEventListener("resize", () => {
  clearTimeout(resizeTimeout);
  resizeTimeout = setTimeout(() => {
    resizeCallbacks.forEach((callback) => callback());
  }, 100);
});

export const addResizeCallback = (callback) => {
  resizeCallbacks.add(callback);
  return () => resizeCallbacks.delete(callback);
};

const watchPreferredMotion = (callback = () => {}) => {
	const mm = gsap.matchMedia();

	mm.add("(prefers-reduced-motion: no-preference)", callback);
};

export const watchDesktop = (callback = () => {}) => {
	const mm = gsap.matchMedia();

	mm.add("(min-width: 992px)", callback);
};

const watchMobile = (callback = () => {}) => {
	const mm = gsap.matchMedia();

	mm.add("(max-width: 991px)", callback);
};
